import type { IFilesystem } from '@livx.cc/wcli/core';
import type { ChatLike, ChatResponse, Message } from './llm';
import { contentText } from './llm';
import type { RunResult } from './Agent';
import { writeFact, slugify } from './memory';
import { forComponent } from './logging';

const log = forComponent('Reflect');

/**
 * One-shot LLM reflection on a finished run. The deterministic `lessonCapture` hook only
 * knows OUR pre-mapped failure patterns; reflection catches NOVEL mistakes — it reads a digest
 * of what the run actually did and, if there's a durable lesson, persists it to memory so the
 * next session is steered. Costs exactly ONE model call, so it's meant for runs that went badly
 * (loop / budget / max_steps), not every run. Opt-in. Returns the slug written, or null.
 */
export interface ReflectOptions {
  ai: ChatLike;
  model: string;
  /** Filesystem + memory dir to persist the lesson into (same dir loadMemory reads). */
  fs: IFilesystem;
  dir: string;
  /** The finished run to reflect on. */
  result: RunResult;
  /** Bound the reflection prompt (chars of run digest). */
  maxDigestChars?: number;
}

export async function reflectOnRun(o: ReflectOptions): Promise<string | null> {
  const digest = digestRun(o.result.messages, o.maxDigestChars ?? 6000);
  if (!digest.trim()) return null;
  const prompt =
    `A coding agent just finished a task with outcome "${o.result.finishReason}". Here is a digest of what it did:\n\n${digest}\n\n` +
    `If there is a DURABLE, reusable lesson that would help a FUTURE session avoid a mistake seen here, reply with exactly one line:\n` +
    `LESSON: <imperative, specific, ≤200 chars>\n` +
    `If the run was fine or the issue was purely task-specific (not generalizable), reply exactly: NONE`;

  let text = '';
  try {
    const r = (await o.ai.chat({ model: o.model, messages: [{ role: 'user', content: prompt }], stream: false })) as ChatResponse;
    text = r?.content ?? '';
  } catch (e: any) {
    log.warn(`reflection call failed: ${e?.message ?? e}`);
    return null;
  }

  const m = text.match(/LESSON:\s*(.+)/i);
  const lesson = m?.[1]?.trim().slice(0, 200) ?? '';
  if (!lesson || /^none\b/i.test(lesson)) return null;

  const slug = ('lesson-' + slugify(lesson)).slice(0, 56);
  try {
    await writeFact(o.fs, o.dir, slug, lesson);
  } catch (e: any) {
    log.warn(`could not persist lesson: ${e?.message ?? e}`);
    return null;
  }
  log.debug(`reflection persisted ${slug}`);
  return slug;
}

/** Compact, bounded digest of a run: assistant intents + tool calls + their results (errors included). */
function digestRun(messages: Message[], maxChars: number): string {
  const lines: string[] = [];
  for (const m of messages) {
    if (m.role === 'assistant' && m.content) lines.push(`assistant: ${contentText(m.content).slice(0, 200)}`);
    for (const tc of m.tool_calls ?? []) lines.push(`tool ${tc.function.name}(${(tc.function.arguments ?? '').slice(0, 120)})`);
    if (m.role === 'tool' && m.content) lines.push(`  → ${contentText(m.content).split('\n')[0].slice(0, 200)}`);
  }
  // defense-in-depth: untrusted run content (file bodies, tool output) is DATA, not instructions —
  // neutralize a literal `LESSON:` so a crafted file can't spoof the reply format we parse below.
  const out = lines.join('\n').replace(/LESSON:/gi, 'lesson(reported):');
  return out.length > maxChars ? out.slice(0, maxChars) + '\n… (truncated)' : out;
}
